import { useState, useEffect } from "react";
import { Loader2, Mail, Copy, Check, Crown } from "lucide-react";
import { api, extractAxiosError } from "../lib/api";
import { toast } from "sonner";
import { Dialog, DialogFooter } from "./EditPercentagesDialog";
import { notifyInviteInboxChanged } from "@/lib/inviteEvents";

export function InviteRecipientDialog({ pool, orgId, open, onClose, onInvited }) {
    const [email, setEmail] = useState("");
    const [roleLabel, setRoleLabel] = useState("");
    const [percentage, setPercentage] = useState("");
    const [poolRole, setPoolRole] = useState("recipient");
    const [link, setLink] = useState("");
    const [emailFailed, setEmailFailed] = useState(false);
    const [copied, setCopied] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) {
            setEmail("");
            setRoleLabel("");
            setPercentage("");
            setPoolRole("recipient");
            setLink("");
            setEmailFailed(false);
            setCopied(false);
            setBusy(false);
        }
    }, [open]);

    if (!open) return null;

    const submit = async (e) => {
        e.preventDefault();
        if (!percentage) {
            toast.error("Allocation percentage is required for pool invites");
            return;
        }
        try {
            setBusy(true);
            const payload = {
                email,
                role: poolRole === "co_owner" ? "admin" : "member",
                org_id: orgId,
                pool_id: pool?.id,
                role_label: roleLabel,
                percentage: parseFloat(percentage),
                pool_role: poolRole,
            };
            const { data } = await api.post("/invitations", payload);
            setLink(data.invite_link);
            setEmailFailed(!data.email_delivery?.ok);
            const emailOk = data.email_delivery?.ok;
            if (emailOk) {
                toast.success("Invitation sent", {
                    description: poolRole === "co_owner"
                        ? `Co-owner invite emailed to ${email}`
                        : `Pool invite emailed to ${email}`,
                });
            } else {
                toast.warning("Invite created — email not delivered", {
                    description: data.email_delivery?.error || "Copy the link below and send it manually.",
                });
            }
            notifyInviteInboxChanged();
            onInvited?.(data.invitation);
        } catch (err) {
            toast.error(extractAxiosError(err, "Failed to send invitation"));
        } finally { setBusy(false); }
    };

    const copy = async () => {
        await navigator.clipboard.writeText(link);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    const isCoOwner = poolRole === "co_owner";

    return (
        <Dialog
            onClose={onClose}
            title={link ? "Invitation sent" : `Invite to ${pool?.name || "pool"}`}
            subtitle={link ? "They can also use the link in their email." : "We'll email them a link — new users create an account; existing users accept in Invite Center."}
            testid="invite-dialog"
        >
            {!link ? (
                <form onSubmit={submit} className="space-y-3" data-testid="invite-form">
                    <div>
                        <label className="label-eyebrow">Email</label>
                        <input
                            type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                            className="input-dark mt-1.5 w-full rounded-md px-3 py-2 text-sm"
                            placeholder="user@company.com"
                            data-testid="invite-email"
                        />
                    </div>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div>
                            <label className="label-eyebrow">Pool role</label>
                            <div className="mt-1.5 flex gap-2">
                                <button
                                    type="button"
                                    onClick={() => setPoolRole("recipient")}
                                    className={`flex-1 rounded-md border px-3 py-2 text-xs ${poolRole === "recipient" ? "border-[#00BF0D]/40 bg-[#00BF0D]/10 text-[#00BF0D]" : "border-white/10 text-white/55"}`}
                                    data-testid="invite-pool-role-recipient"
                                >
                                    Member
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setPoolRole("co_owner")}
                                    className={`flex-1 inline-flex items-center justify-center gap-1 rounded-md border px-3 py-2 text-xs ${isCoOwner ? "border-[#5D00FF]/40 bg-[#5D00FF]/15 text-[#B794FF]" : "border-white/10 text-white/55"}`}
                                    data-testid="invite-pool-role-coowner"
                                >
                                    <Crown className="h-3 w-3" /> Co-owner
                                </button>
                            </div>
                        </div>
                        <div>
                            <label className="label-eyebrow">Role label</label>
                            <input
                                value={roleLabel} onChange={(e) => setRoleLabel(e.target.value)} placeholder="Founder, CFO…"
                                className="input-dark mt-1.5 w-full rounded-md px-3 py-2 text-sm"
                                data-testid="invite-role-label"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="label-eyebrow">Share in "{pool?.name}"</label>
                        <div className="mt-1.5 relative">
                            <input
                                type="number" min="0" max="100" step="0.01" value={percentage}
                                onChange={(e) => setPercentage(e.target.value)}
                                required
                                placeholder="e.g. 25"
                                className="input-dark w-full rounded-md px-3 py-2 pr-8 text-sm font-mono"
                                data-testid="invite-percentage"
                            />
                            <span className="absolute right-3 inset-y-0 flex items-center text-white/40 text-sm">%</span>
                        </div>
                        <p className="text-[11px] text-white/35 mt-1">Rebalance other members so the pool total stays at 100%.</p>
                    </div>
                    <DialogFooter>
                        <button type="button" onClick={onClose} className="btn-ghost rounded-md px-4 py-2 text-sm">Cancel</button>
                        <button type="submit" disabled={busy} className="btn-neon rounded-md px-4 py-2 text-sm inline-flex items-center gap-2" data-testid="invite-submit">
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                            {busy ? "Sending…" : isCoOwner ? "Send co-owner invite" : "Send invite"}
                        </button>
                    </DialogFooter>
                </form>
            ) : (
                <div className="space-y-4" data-testid="invite-link-block">
                    <div className="rounded-lg border border-white/[0.08] bg-[#1A1A1A] p-3 flex items-center gap-2">
                        <code className="text-xs text-white/75 flex-1 truncate font-mono">{link}</code>
                        <button onClick={copy} className="btn-ghost rounded-md px-2.5 py-1.5 text-xs inline-flex items-center gap-1" data-testid="invite-copy-link">
                            {copied ? <Check className="h-3.5 w-3.5 text-[#00BF0D]" /> : <Copy className="h-3.5 w-3.5" />}
                            {copied ? "Copied" : "Copy"}
                        </button>
                    </div>
                    <p className="text-xs text-white/45">
                        {isCoOwner ? "Co-owner" : "Pool"} invitation for {email}.
                        {emailFailed ? (
                            <span className="block mt-1 text-amber-400/90">Email failed — share the link manually.</span>
                        ) : (
                            " New users sign up from the link; existing users can accept in Invite Center."
                        )}
                    </p>
                    <DialogFooter>
                        <button onClick={onClose} className="btn-neon rounded-md px-4 py-2 text-sm" data-testid="invite-done">Done</button>
                    </DialogFooter>
                </div>
            )}
        </Dialog>
    );
}
